import React from 'react';

/**
 * Iter 66: Click-to-Chat WhatsApp helper.
 *
 * Builds a tel-free wa.me URL with a pre-filled message.  UK numbers are
 * normalised to +44 (strip leading 0, prepend country code) so that the link
 * works on both desktop and mobile WhatsApp clients.
 *
 * Usage: <WhatsAppButton phone={customer.phone} name={customer.name} service={enquiry.service} />
 *
 * Iter 66.1: HARDENED — every input is run through String() before any
 * regex / split call, so a non-string phone (e.g. number from a legacy
 * import) cannot crash the parent React tree.
 */
const safeStr = (v) => {
  if (v === null || v === undefined) return '';
  try { return String(v); } catch { return ''; }
};

export const buildWhatsAppUrl = ({ phone, name, service }) => {
  const phoneStr = safeStr(phone);
  if (!phoneStr) return null;

  // Strip everything except digits; assume UK numbers default to 44 prefix.
  let digits = phoneStr.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) digits = digits.slice(2);          // 0044xxx → 44xxx
  else if (digits.startsWith('0')) digits = '44' + digits.slice(1); // 07xxx   → 447xxx
  // Already includes country code (>=11 digits starting non-zero) — leave alone.

  const nameStr = safeStr(name);
  const firstName = nameStr.trim().split(/\s+/)[0] || 'there';
  const svc = safeStr(service).replace(/[-_]/g, ' ').trim();
  const svcFragment = svc ? ` regarding your ${svc} project` : '';
  const body = `Hi ${firstName}, it's Sandy from Sustainable Handyman Services${svcFragment}...`;

  return `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
};

export const WhatsAppButton = ({ phone, name, service, size = 'sm', className = '', onClickCapture }) => {
  let url = null;
  try {
    url = buildWhatsAppUrl({ phone, name, service });
  } catch (e) {
    // Defensive: never let this helper crash the dialog tree.
    // eslint-disable-next-line no-console
    console.warn('[WhatsAppButton] URL build failed:', e);
    return null;
  }
  if (!url) return null;
  const phoneDigits = safeStr(phone).replace(/\D/g, '');
  const dims = size === 'lg' ? 'h-6 w-6' : size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        if (onClickCapture) onClickCapture(e);
      }}
      title={`Message ${safeStr(name) || 'customer'} on WhatsApp`}
      aria-label="Open WhatsApp chat"
      data-testid={`whatsapp-link-${phoneDigits}`}
      className={`inline-flex items-center justify-center rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white transition-colors ${size === 'lg' ? 'p-2' : 'p-1.5'} ${className}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={dims} aria-hidden="true">
        <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.99L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52zM12 22a9.94 9.94 0 0 1-5.06-1.39l-.36-.21-3.67.96.98-3.57-.24-.37A9.94 9.94 0 1 1 22 12c0 5.51-4.49 10-10 10zm5.49-7.47c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.64-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.88 1.21 3.08.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.63.71.23 1.35.2 1.86.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"/>
      </svg>
    </a>
  );
};

export default WhatsAppButton;
